import type { HoverIdleSnapshot } from "@/features/hoverIdle/types";
import { achievementDefinitions } from "./achievementDefinitions";
import {
  contributesToAchievementCountMultiplier,
  computeHovGainAchievementCountMultiplier,
  HOV_GAIN_ACHIEVEMENT_COUNT_BASE_DEFAULT,
  computeTimGainAchievementCountMultiplier,
  TIM_GAIN_ACHIEVEMENT_COUNT_BASE_DEFAULT,
} from "./achievementEffects";
import type {
  AchievementCategory,
  AchievementDefinition,
  AchievementId,
  AchievementsSnapshot,
  AchievementsState,
} from "./types";
import { normalizeAchievementsState } from "./achievementIdAliases";

export interface AchievementsModelOptions {
  readonly definitions?: readonly AchievementDefinition[];
}

export class AchievementsModel {
  private readonly definitions: readonly AchievementDefinition[];
  private readonly definitionById: ReadonlyMap<
    AchievementId,
    AchievementDefinition
  >;
  private readonly unlocked = new Set<AchievementId>();
  private readonly seen = new Set<AchievementId>();

  constructor(options: AchievementsModelOptions = {}) {
    this.definitions = options.definitions ?? achievementDefinitions;
    this.definitionById = new Map(this.definitions.map((d) => [d.id, d]));
  }

  getDefinition(id: AchievementId): AchievementDefinition | undefined {
    return this.definitionById.get(id);
  }

  isUnlocked(id: AchievementId): boolean {
    return this.unlocked.has(id);
  }

  evaluate(snapshot: HoverIdleSnapshot): readonly AchievementId[] {
    const newlyUnlocked: AchievementId[] = [];
    for (const def of this.definitions) {
      if (this.unlocked.has(def.id)) continue;
      if (def.isUnlocked(snapshot)) {
        this.unlocked.add(def.id);
        newlyUnlocked.push(def.id);
      }
    }
    return newlyUnlocked;
  }

  markSeen(id: AchievementId): boolean {
    if (!this.unlocked.has(id) || this.seen.has(id)) return false;
    this.seen.add(id);
    return true;
  }

  markAllUnlockedSeen(): boolean {
    let changed = false;
    for (const id of this.unlocked) {
      if (!this.seen.has(id)) {
        this.seen.add(id);
        changed = true;
      }
    }
    return changed;
  }

  markUnlockedSeenByCategory(category: AchievementCategory): boolean {
    let changed = false;
    for (const id of this.unlocked) {
      const definition = this.definitionById.get(id);
      if (definition?.category === category && !this.seen.has(id)) {
        this.seen.add(id);
        changed = true;
      }
    }
    return changed;
  }

  hasUnseen(category?: AchievementCategory): boolean {
    for (const id of this.unlocked) {
      const definition = this.definitionById.get(id);
      if (
        !this.seen.has(id) &&
        (category === undefined || definition?.category === category)
      ) {
        return true;
      }
    }
    return false;
  }

  getSnapshot(): AchievementsSnapshot {
    let unseenCount = 0;
    let normalUnseenCount = 0;
    let secretUnseenCount = 0;
    let normalUnlockedCount = 0;
    let normalCountMultiplierCount = 0;
    let normalDisplayIndex = 0;
    let secretUnlockedCount = 0;
    let secretCountMultiplierCount = 0;
    let secretDisplayIndex = 0;
    const entries = this.definitions.map((definition) => {
      const unlocked = this.unlocked.has(definition.id);
      const seen = this.seen.has(definition.id);
      const displayIndex =
        definition.category === "secret"
          ? (secretDisplayIndex += 1)
          : (normalDisplayIndex += 1);
      if (unlocked && !seen) {
        unseenCount += 1;
        if (definition.category === "secret") {
          secretUnseenCount += 1;
        } else {
          normalUnseenCount += 1;
        }
      }
      if (unlocked) {
        if (definition.category === "secret") {
          secretUnlockedCount += 1;
          if (contributesToAchievementCountMultiplier(definition.id)) {
            secretCountMultiplierCount += 1;
          }
        } else {
          normalUnlockedCount += 1;
          if (contributesToAchievementCountMultiplier(definition.id)) {
            normalCountMultiplierCount += 1;
          }
        }
      }
      return { definition, displayIndex, unlocked, seen };
    });
    const hovGainCountMultiplierBase = HOV_GAIN_ACHIEVEMENT_COUNT_BASE_DEFAULT;
    const hovGainCountMultiplier = computeHovGainAchievementCountMultiplier(
      normalCountMultiplierCount,
      hovGainCountMultiplierBase,
    );
    const timGainCountMultiplierBase = TIM_GAIN_ACHIEVEMENT_COUNT_BASE_DEFAULT;
    const timGainCountMultiplier = computeTimGainAchievementCountMultiplier(
      secretCountMultiplierCount,
      timGainCountMultiplierBase,
    );
    return {
      entries,
      unseenCount,
      normalUnseenCount,
      secretUnseenCount,
      normalUnlockedCount,
      secretUnlockedCount,
      hovGainCountMultiplier,
      hovGainCountMultiplierBase,
      timGainCountMultiplier,
      timGainCountMultiplierBase,
    };
  }

  exportState(): AchievementsState {
    return {
      unlockedIds: [...this.unlocked],
      seenIds: [...this.seen],
    };
  }

  restoreState(state: AchievementsState): void {
    this.unlocked.clear();
    this.seen.clear();
    const normalizedState = normalizeAchievementsState({
      unlockedIds: state.unlockedIds as readonly string[],
      seenIds: state.seenIds as readonly string[],
    });
    for (const id of normalizedState.unlockedIds) {
      if (this.definitionById.has(id)) this.unlocked.add(id);
    }
    for (const id of normalizedState.seenIds) {
      if (this.definitionById.has(id) && this.unlocked.has(id)) {
        this.seen.add(id);
      }
    }
  }
}
